import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ImpersonationService } from 'app/core/impersonation/impersonation.service';
import { ImpersonationSearchDialogComponent } from 'app/core/impersonation/impersonation-search-dialog/impersonation-search-dialog.component';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LOCAL_USER_KEY } from 'global';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    imports: [MatButtonModule, MatMenuModule, MatIconModule, NgClass, MatDividerModule, RouterLink, TranslocoPipe],
})
export class UserComponent implements OnInit, OnDestroy {
    static ngAcceptInputType_showAvatar: BooleanInput;

    @Input() showAvatar: boolean = true;
    user: User;
    /** ¿El usuario actual (super_admin, no impersonando) puede impersonar? Calculado, no getter por-CD. */
    canImpersonate = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _impersonation: ImpersonationService,
        private _dialog: MatDialog
    ) {
        this.user = JSON.parse(window.localStorage.getItem(LOCAL_USER_KEY) ?? '{}');
        this.canImpersonate = this._impersonation.canImpersonate();
        // this._changeDetectorRef.markForCheck();
    }

    /** Abre el diálogo de búsqueda para iniciar una impersonation. */
    openImpersonate(): void {
        this._dialog.open(ImpersonationSearchDialogComponent, {
            autoFocus: true,
            restoreFocus: true,
            width: 'min(36rem, 92vw)',
            maxWidth: '92vw',
            panelClass: 'impersonation-dialog',
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
            this.user = user;
            this.canImpersonate = this._impersonation.canImpersonate();

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
                status,
            } as any)
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }
}
